import React, { useState, useCallback } from 'react';
import { useDeletedBusinesses, DeletedBusiness } from './api/useDeletedBusinesses';
import { useRecoverBusiness } from './api/useRecoverBusiness';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner, SuccessBanner } from '../shared';

// ─────────────────────────────────────────────────────────────────────────────
// Recover Confirm Modal
// ─────────────────────────────────────────────────────────────────────────────

function RecoverConfirmModal({ business, onClose, onConfirm, loading }: { business: DeletedBusiness; onClose: () => void; onConfirm: () => void; loading: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col max-h-[92vh] sm:my-4">
        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 flex-1 overflow-y-auto">
          <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-950/50 border border-emerald-800/50 flex items-center justify-center">
            <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white">Recover Business</h3>
            <p className="text-sm text-gray-400 mt-1">
              Are you sure you want to recover <span className="text-white font-medium">{business.name}</span>? This will restore the business and make it active again.
            </p>
          </div>
        </div>
        <div className="flex gap-3 px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0">
          <button onClick={onClose} disabled={loading} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Spinner size="sm" />Recovering…</> : 'Recover'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Business Recovery Tab
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export function BusinessRecoveryTab() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [page, setPage] = useState(0);
  const [recoveringBusiness, setRecoveringBusiness] = useState<DeletedBusiness | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeApiUrl = getAdminApiUrl(selectedEnv);

  const { businesses, total, loading, error, refetch } = useDeletedBusinesses({ search: debouncedSearch, page, limit: PAGE_SIZE, apiUrl: activeApiUrl });
  const { loading: recoverLoading, error: recoverError, recoverBusiness } = useRecoverBusiness();

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  const handleEnvChange = useCallback((env: Env) => {
    setSelectedEnv(env);
    setPage(0);
  }, []);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  const handleRecover = useCallback(async () => {
    if (!recoveringBusiness) return;
    const ok = await recoverBusiness(recoveringBusiness.id, activeApiUrl);
    if (ok) {
      setRecoveringBusiness(null);
      if (businesses.length === 1 && page > 0) setPage(p => p - 1);
      else refetch();
      showSuccess(`${recoveringBusiness.name} recovered successfully.`);
    }
  }, [recoveringBusiness, recoverBusiness, activeApiUrl, businesses.length, page, refetch, showSuccess]);

  return (
    <>
      {/* Toolbar: Search + Env switcher */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input type="text" value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search by name or email…" className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all" />
        </div>
        {!loading && total > 0 && <span className="text-sm text-gray-500">{total} total</span>}

        {/* Env switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const envApiUrl = getAdminApiUrl(env);
            const isConfigured = Boolean(envApiUrl);
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && handleEnvChange(env)}
                title={isConfigured ? undefined : 'Not configured'}
                disabled={!isConfigured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {/* PROD badge */}
        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {(error || recoverError) && <ErrorBanner message={error || recoverError!} />}

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Header row */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-800">
          <div className="w-10 flex-shrink-0" />
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</p>
          <p className="hidden sm:block flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Deleted</p>
          <div className="flex-shrink-0 w-24" />
        </div>

        {loading && <div className="py-16 text-center flex flex-col items-center gap-3"><Spinner size="lg" /><p className="text-sm text-gray-500">Loading deleted businesses…</p></div>}
        {!loading && businesses.length === 0 && <div className="py-16 text-center"><p className="text-gray-500 text-sm">{search ? 'No deleted businesses match your search.' : 'No deleted businesses found.'}</p></div>}

        {!loading && businesses.map((biz, i) => (
          <div key={biz.id} className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 active:bg-gray-800/50 transition-colors group ${i < businesses.length - 1 ? 'border-b border-gray-800' : ''}`}>
            <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-700">
              {biz.business_logo
                ? <img src={biz.business_logo} alt={biz.name} className="w-full h-full object-cover" />
                : <span className="text-sm font-bold text-gray-400">{biz.name.charAt(0).toUpperCase()}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{biz.name}</p>
              <p className="text-xs text-gray-500 truncate">{biz.address || '—'}</p>
              <p className="sm:hidden text-xs text-gray-500 mt-0.5 tabular-nums">
                Deleted {new Date(biz.deleted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <div className="hidden sm:flex items-center flex-shrink-0">
              <span className="text-xs text-gray-500 tabular-nums">
                {new Date(biz.deleted_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
              <button
                onClick={() => setRecoveringBusiness(biz)}
                className="px-3 py-1.5 text-xs font-medium text-emerald-300 bg-emerald-950/30 border border-emerald-800/50 rounded-lg hover:text-white hover:border-emerald-500 hover:bg-emerald-900/40 active:bg-emerald-900/40 transition-all"
              >
                Recover
              </button>
            </div>
          </div>
        ))}
      </div>

      {!loading && total > 0 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-gray-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + businesses.length, total)} of {total} deleted businesses
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-500 tabular-nums">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {recoveringBusiness && (
        <RecoverConfirmModal
          business={recoveringBusiness}
          onClose={() => setRecoveringBusiness(null)}
          onConfirm={handleRecover}
          loading={recoverLoading}
        />
      )}
    </>
  );
}
